import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Search, Download, Mail, Eye, Users, MapPin, BarChart3, Building2, Loader2, PlayCircle, ArrowUp, ArrowDown, ArrowUpDown, Trash2, Send } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import * as XLSX from "xlsx";
import { MailingRecordDetail } from "./MailingRecordDetail";
import { BusinessLookupDialog } from "./BusinessLookupDialog";

interface MailingRecord {
  id: string;
  seller_id: string;
  county: string | null;
  origin_state: string | null;
  first_name: string | null;
  last_name: string | null;
  business_name: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  consideration: number | null;
  document_type: string | null;
  property_address: string | null;
  response: string | null;
  phone_number: string | null;
  email_address: string | null;
  mailing_status: string;
  times_mailed: number;
  last_mailed_date: string | null;
  recorded_date: string | null;
  tm_notes: string | null;
  tm_dropdown: string | null;
}

export function MailingRecordsTable({ refreshKey }: { refreshKey: number }) {
  const [records, setRecords] = useState<MailingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [countyFilter, setCountyFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [docTypeFilter, setDocTypeFilter] = useState("all");
  const [mailedFilter, setMailedFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const [responseFilter, setResponseFilter] = useState("all");
  const [recordedBefore, setRecordedBefore] = useState("");
  const [lastMailedBefore, setLastMailedBefore] = useState("");
  const [counties, setCounties] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [docTypes, setDocTypes] = useState<string[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<MailingRecord | null>(null);
  const [lookupRecord, setLookupRecord] = useState<MailingRecord | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [sortColumn, setSortColumn] = useState<string>("last_name");
  const [sortAsc, setSortAsc] = useState(true);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0, current: '' });
  const [commitCampaign, setCommitCampaign] = useState("");
  const [commitDate, setCommitDate] = useState(new Date().toISOString().split("T")[0]);
  const [commitRunning, setCommitRunning] = useState(false);
  const [showCommitDialog, setShowCommitDialog] = useState(false);
  const PAGE_SIZE = 100;

  // Stats
  const [stats, setStats] = useState({ total: 0, responded: 0, unmailed: 0 });

  useEffect(() => {
    fetchFilterOptions();
    fetchStats();
  }, [refreshKey]);

  useEffect(() => {
    fetchRecords();
  }, [search, countyFilter, stateFilter, statusFilter, docTypeFilter, mailedFilter, entityFilter, responseFilter, recordedBefore, lastMailedBefore, page, sortColumn, sortAsc, refreshKey]);

  const fetchFilterOptions = async () => {
    // Fetch ALL records in batches to get complete distinct values
    let allCounties = new Set<string>();
    let allStates = new Set<string>();
    let allDocTypes = new Set<string>();
    let page = 0;
    const pageSize = 1000;
    let hasMore = true;

    while (hasMore) {
      const { data } = await supabase
        .from("mailing_records")
        .select("county, origin_state, document_type")
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (!data || data.length === 0) {
        hasMore = false;
      } else {
        data.forEach((r: any) => {
          if (r.county) allCounties.add(r.county);
          if (r.origin_state) allStates.add(r.origin_state);
          if (r.document_type) allDocTypes.add(r.document_type);
        });
        hasMore = data.length === pageSize;
        page++;
      }
    }

    setCounties([...allCounties].sort());
    setStates([...allStates].sort());
    setDocTypes([...allDocTypes].sort());
  };

  const fetchStats = async () => {
    const { count: total } = await supabase.from("mailing_records").select("*", { count: "exact", head: true });
    const { count: responded } = await supabase.from("mailing_records").select("*", { count: "exact", head: true }).not("response", "is", null).neq("response", "");
    const { count: unmailed } = await supabase.from("mailing_records").select("*", { count: "exact", head: true }).eq("times_mailed", 0);
    setStats({ total: total || 0, responded: responded || 0, unmailed: unmailed || 0 });
  };

  const fetchRecords = async () => {
    setLoading(true);
    let query = supabase.from("mailing_records").select("*", { count: "exact" });

    if (search) {
      query = query.or(`last_name.ilike.%${search}%,first_name.ilike.%${search}%,seller_id.ilike.%${search}%,city.ilike.%${search}%,property_address.ilike.%${search}%`);
    }
    if (countyFilter !== "all") query = query.eq("county", countyFilter);
    if (stateFilter !== "all") query = query.eq("origin_state", stateFilter);
    if (statusFilter !== "all") query = query.eq("mailing_status", statusFilter);
    if (docTypeFilter !== "all") query = query.eq("document_type", docTypeFilter);
    if (mailedFilter === "0") query = query.eq("times_mailed", 0);
    else if (mailedFilter === "1") query = query.eq("times_mailed", 1);
    else if (mailedFilter === "2+") query = query.gte("times_mailed", 2);
    if (entityFilter === "business") query = query.not("business_name", "is", null).neq("business_name", "");
    else if (entityFilter === "individual") query = query.or("business_name.is.null,business_name.eq.");
    if (responseFilter === "none") query = query.or("response.is.null,response.eq.");
    else if (responseFilter === "has") query = query.not("response", "is", null).neq("response", "");
    if (recordedBefore) query = query.lte("recorded_date", recordedBefore);
    if (lastMailedBefore) query = query.lte("last_mailed_date", lastMailedBefore);

    const { data, count, error } = await query
      .order(sortColumn, { ascending: sortAsc })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

    if (error) {
      toast.error("Failed to fetch records");
      console.error(error);
    } else {
      setRecords(data || []);
      setTotalCount(count || 0);
    }
    setLoading(false);
  };

  const buildFilteredQuery = () => {
    let query = supabase.from("mailing_records").select("*");
    if (search) query = query.or(`last_name.ilike.%${search}%,first_name.ilike.%${search}%,seller_id.ilike.%${search}%,city.ilike.%${search}%,property_address.ilike.%${search}%`);
    if (countyFilter !== "all") query = query.eq("county", countyFilter);
    if (stateFilter !== "all") query = query.eq("origin_state", stateFilter);
    if (statusFilter !== "all") query = query.eq("mailing_status", statusFilter);
    if (docTypeFilter !== "all") query = query.eq("document_type", docTypeFilter);
    if (mailedFilter === "0") query = query.eq("times_mailed", 0);
    else if (mailedFilter === "1") query = query.eq("times_mailed", 1);
    else if (mailedFilter === "2+") query = query.gte("times_mailed", 2);
    if (entityFilter === "business") query = query.not("business_name", "is", null).neq("business_name", "");
    else if (entityFilter === "individual") query = query.or("business_name.is.null,business_name.eq.");
    if (responseFilter === "none") query = query.or("response.is.null,response.eq.");
    else if (responseFilter === "has") query = query.not("response", "is", null).neq("response", "");
    if (recordedBefore) query = query.lte("recorded_date", recordedBefore);
    if (lastMailedBefore) query = query.lte("last_mailed_date", lastMailedBefore);
    return query;
  };

  const handleExport = async () => {
    let allRecords: any[] = [];
    let from = 0;
    const pageSize = 1000;
    while (true) {
      const query = buildFilteredQuery();
      const { data, error } = await query
        .order(sortColumn, { ascending: sortAsc })
        .range(from, from + pageSize - 1);
      if (error || !data || data.length === 0) break;
      allRecords = [...allRecords, ...data];
      if (data.length < pageSize) break;
      from += pageSize;
    }
    if (!allRecords.length) { toast.error("No records to export"); return; }
    const toProperCase = (s: string | null) => {
      if (!s) return "";
      return s.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substring(1).toLowerCase());
    };
    const rows = allRecords.map((r) => ({
      "First Name": toProperCase(r.first_name),
      "Last Name": toProperCase(r.last_name),
      "Business Name": r.business_name || "",
      "Street Address": r.street_address || "",
      "City": toProperCase(r.city),
      "State": (r.state || "").toUpperCase(),
      "Zip Code": r.zip_code || "",
      "County": toProperCase(r.county),
      "Origin State": (r.origin_state || "").toUpperCase(),
      "Document Type": r.document_type || "",
      "Recorded Date": r.recorded_date || "",
      "Consideration": r.consideration || "",
      "Times Mailed": r.times_mailed,
      "Last Mailed": r.last_mailed_date || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 16 }, { wch: 20 }, { wch: 24 }, { wch: 28 }, { wch: 16 },
      { wch: 6 }, { wch: 10 }, { wch: 14 }, { wch: 6 }, { wch: 18 },
      { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 12 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mailing Records");
    const dateStr = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `Mailing_Export_${dateStr}.xlsx`);
    toast.success(`Exported ${rows.length} records to Excel`);
  };

  const handleMailExport = async () => {
    let allRecords: any[] = [];
    let from = 0;
    const pageSize = 1000;
    while (true) {
      const query = buildFilteredQuery();
      const { data, error } = await query
        .order(sortColumn, { ascending: sortAsc })
        .range(from, from + pageSize - 1);
      if (error || !data || data.length === 0) break;
      allRecords = [...allRecords, ...data];
      if (data.length < pageSize) break;
      from += pageSize;
    }
    if (!allRecords.length) { toast.error("No records to export"); return; }
    const toProperCase = (s: string | null) => {
      if (!s) return "";
      return s.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substring(1).toLowerCase());
    };
    const rows = allRecords.map((r) => ({
      "First Name": toProperCase(r.first_name),
      "Last Name": toProperCase(r.last_name),
      "Street Address": r.street_address || "",
      "City": toProperCase(r.city),
      "State": (r.state || "").toUpperCase(),
      "Zip Code": r.zip_code || "",
      "County": toProperCase(r.county),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 18 }, { wch: 22 }, { wch: 30 }, { wch: 18 },
      { wch: 8 }, { wch: 12 }, { wch: 16 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mail List");
    const dateStr = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `Mail_List_${dateStr}.xlsx`);
    toast.success(`Mail export: ${rows.length} records`);
  };

  const handleCommitToMail = async () => {
    if (!commitCampaign.trim()) { toast.error("Enter a campaign/mailing name"); return; }
    if (!commitDate) { toast.error("Enter a mail date"); return; }

    setCommitRunning(true);
    try {
      // Fetch all filtered records with id and times_mailed
      let allRecords: { id: string; times_mailed: number }[] = [];
      let from = 0;
      const pageSize = 1000;
      while (true) {
        let query = supabase.from("mailing_records").select("id, times_mailed");
        if (search) query = query.or(`last_name.ilike.%${search}%,first_name.ilike.%${search}%,seller_id.ilike.%${search}%,city.ilike.%${search}%,property_address.ilike.%${search}%`);
        if (countyFilter !== "all") query = query.eq("county", countyFilter);
        if (stateFilter !== "all") query = query.eq("origin_state", stateFilter);
        if (statusFilter !== "all") query = query.eq("mailing_status", statusFilter);
        if (docTypeFilter !== "all") query = query.eq("document_type", docTypeFilter);
        if (mailedFilter === "0") query = query.eq("times_mailed", 0);
        else if (mailedFilter === "1") query = query.eq("times_mailed", 1);
        else if (mailedFilter === "2+") query = query.gte("times_mailed", 2);
        if (entityFilter === "business") query = query.not("business_name", "is", null).neq("business_name", "");
        else if (entityFilter === "individual") query = query.or("business_name.is.null,business_name.eq.");
        if (responseFilter === "none") query = query.or("response.is.null,response.eq.");
        else if (responseFilter === "has") query = query.not("response", "is", null).neq("response", "");
        if (recordedBefore) query = query.lte("recorded_date", recordedBefore);
        if (lastMailedBefore) query = query.lte("last_mailed_date", lastMailedBefore);
        const { data, error } = await query.range(from, from + pageSize - 1);
        if (error || !data || data.length === 0) break;
        allRecords = [...allRecords, ...data];
        if (data.length < pageSize) break;
        from += pageSize;
      }

      if (allRecords.length === 0) {
        toast.error("No records match current filters");
        setCommitRunning(false);
        return;
      }

      // Process in batches
      const batchSize = 500;
      let created = 0;
      for (let i = 0; i < allRecords.length; i += batchSize) {
        const batch = allRecords.slice(i, i + batchSize);

        // Create mailing_history entries
        const historyRows = batch.map(r => ({
          mailing_record_id: r.id,
          campaign_name: commitCampaign.trim(),
          mailed_date: commitDate,
          mailing_round: r.times_mailed + 1,
          letter_type: "first-class",
        }));

        const { error: histError } = await supabase.from("mailing_history").insert(historyRows);
        if (histError) { console.error(histError); continue; }

        // Update each record's times_mailed and last_mailed_date
        for (const rec of batch) {
          await supabase
            .from("mailing_records")
            .update({
              times_mailed: rec.times_mailed + 1,
              last_mailed_date: commitDate,
            })
            .eq("id", rec.id);
        }

        created += batch.length;
      }

      toast.success(`✅ Committed ${created} records as mailed — "${commitCampaign}" on ${commitDate}`);
      setShowCommitDialog(false);
      setCommitCampaign("");
      fetchRecords();
      fetchStats();
    } catch (err) {
      console.error(err);
      toast.error("Commit failed");
    } finally {
      setCommitRunning(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toTitleCase = (str: string) =>
    str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

  const handleApplyLookup = async (recordId: string, contactName: string) => {
    const parts = contactName.trim().split(/\s+/);
    const firstName = toTitleCase(parts[0] || '');
    const lastName = toTitleCase(parts.slice(1).join(' ') || '');

    const { error } = await supabase
      .from('mailing_records')
      .update({ first_name: firstName, last_name: lastName })
      .eq('id', recordId);

    if (error) {
      toast.error('Failed to update record');
    } else {
      toast.success(`Updated contact to ${contactName}`);
      fetchRecords();
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const { error } = await supabase.from('mailing_records').delete().in('id', ids);
    if (error) {
      toast.error('Failed to delete records');
    } else {
      toast.success(`Deleted ${ids.length} record(s)`);
      setSelectedIds(new Set());
      fetchRecords();
      fetchStats();
    }
  };

  const handleDeleteSingle = async (id: string) => {
    const { error } = await supabase.from('mailing_records').delete().eq('id', id);
    if (error) {
      toast.error('Failed to delete record');
    } else {
      toast.success('Record deleted');
      fetchRecords();
      fetchStats();
    }
  };

  const selectedBusinessRecords = records.filter(r => selectedIds.has(r.id) && r.business_name);

  const handleBatchLookup = async () => {
    if (selectedBusinessRecords.length === 0) {
      toast.error('No business records selected');
      return;
    }

    setBatchRunning(true);
    const total = selectedBusinessRecords.length;
    setBatchProgress({ done: 0, total, current: '' });
    let successCount = 0;

    for (let i = 0; i < total; i++) {
      const r = selectedBusinessRecords[i];
      setBatchProgress({ done: i, total, current: r.business_name || '' });

      try {
        const { data, error: fnError } = await supabase.functions.invoke('lookup-business-entity', {
          body: { business_name: r.business_name, state: r.origin_state },
        });

        if (fnError || !data?.success || !data?.data?.contact_name) continue;

        const contactName = data.data.contact_name;
        const parts = contactName.trim().split(/\s+/);
        const firstName = toTitleCase(parts[0] || '');
        const lastName = toTitleCase(parts.slice(1).join(' ') || '');

        const { error: updateError } = await supabase
          .from('mailing_records')
          .update({ first_name: firstName, last_name: lastName })
          .eq('id', r.id);

        if (!updateError) successCount++;
      } catch {
        // skip failures silently
      }
    }

    setBatchProgress({ done: total, total, current: '' });
    setBatchRunning(false);
    setSelectedIds(new Set());
    toast.success(`Updated ${successCount} of ${total} records`);
    fetchRecords();
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortAsc(!sortAsc);
    } else {
      setSortColumn(column);
      setSortAsc(true);
    }
    setPage(0);
  };

  const SortableHead = ({ column, label }: { column: string; label: string }) => (
    <TableHead className="py-1.5 cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort(column)}>
      <div className="flex items-center gap-1">
        {label}
        {sortColumn === column ? (
          sortAsc ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="cursor-pointer hover:ring-2 ring-primary transition-all" onClick={() => { setMailedFilter("all"); setStatusFilter("all"); setPage(0); }}>
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="h-5 w-5 text-primary" />
            <div>
              <p className="text-2xl font-bold">{stats.total.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Total Records</p>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:ring-2 ring-primary transition-all" onClick={() => { setStatusFilter("responded"); setMailedFilter("all"); setPage(0); }}>
          <CardContent className="p-4 flex items-center gap-3">
            <BarChart3 className="h-5 w-5 text-primary" />
            <div>
              <p className="text-2xl font-bold">{stats.responded.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Responded</p>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:ring-2 ring-primary transition-all" onClick={() => { setMailedFilter("0"); setStatusFilter("all"); setPage(0); }}>
          <CardContent className="p-4 flex items-center gap-3">
            <Mail className="h-5 w-5 text-destructive" />
            <div>
              <p className="text-2xl font-bold">{stats.unmailed.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Never Mailed</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <MapPin className="h-5 w-5 text-primary" />
            <div>
              <p className="text-2xl font-bold">{counties.length}</p>
              <p className="text-xs text-muted-foreground">Counties</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search name, ID, city, address..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-9" />
            </div>
            <Select value={countyFilter} onValueChange={(v) => { setCountyFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="County" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Counties</SelectItem>
                {counties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={stateFilter} onValueChange={(v) => { setStateFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[120px]"><SelectValue placeholder="State" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={docTypeFilter} onValueChange={(v) => { setDocTypeFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Doc Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Doc Types</SelectItem>
                {docTypes.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="responded">Responded</SelectItem>
                <SelectItem value="do_not_mail">Do Not Mail</SelectItem>
              </SelectContent>
            </Select>
            <Select value={mailedFilter} onValueChange={(v) => { setMailedFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Times Mailed" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Mailed</SelectItem>
                <SelectItem value="0">Never Mailed</SelectItem>
                <SelectItem value="1">Mailed Once</SelectItem>
                <SelectItem value="2+">Mailed 2+</SelectItem>
              </SelectContent>
            </Select>
            <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Entity Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="business">Business Only</SelectItem>
                <SelectItem value="individual">Individual Only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={responseFilter} onValueChange={(v) => { setResponseFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Response" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Responses</SelectItem>
                <SelectItem value="none">No Response</SelectItem>
                <SelectItem value="has">Has Response</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Recorded ≤</span>
              <Input type="date" value={recordedBefore} onChange={(e) => { setRecordedBefore(e.target.value); setPage(0); }} className="w-[145px] h-9" />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Last Mailed ≤</span>
              <Input type="date" value={lastMailedBefore} onChange={(e) => { setLastMailedBefore(e.target.value); setPage(0); }} className="w-[145px] h-9" />
            </div>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-1" /> Export ({totalCount.toLocaleString()})
            </Button>
            <Button size="sm" variant="secondary" onClick={handleMailExport} disabled={totalCount === 0}>
              <Send className="h-4 w-4 mr-1" /> Mail Export ({totalCount.toLocaleString()})
            </Button>
            {(countyFilter !== "all" || stateFilter !== "all" || statusFilter !== "all" || docTypeFilter !== "all" || mailedFilter !== "all" || entityFilter !== "all" || responseFilter !== "all" || recordedBefore || lastMailedBefore || search) && (
              <Button variant="ghost" size="sm" onClick={() => {
                setSearch(""); setCountyFilter("all"); setStateFilter("all"); setStatusFilter("all");
                setDocTypeFilter("all"); setMailedFilter("all"); setEntityFilter("all");
                setResponseFilter("all"); setRecordedBefore(""); setLastMailedBefore(""); setPage(0);
              }}>
                Clear Filters
              </Button>
            )}
            {selectedIds.size > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="h-4 w-4 mr-1" /> Delete ({selectedIds.size})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {selectedIds.size} record(s)?</AlertDialogTitle>
                    <AlertDialogDescription>This action cannot be undone. The selected mailing records will be permanently deleted.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteSelected}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {selectedBusinessRecords.length > 0 && (
              <Button
                variant="default"
                size="sm"
                onClick={handleBatchLookup}
                disabled={batchRunning}
              >
                {batchRunning ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    {batchProgress.done}/{batchProgress.total}
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-4 w-4 mr-1" />
                    Batch Lookup ({selectedBusinessRecords.length})
                  </>
                )}
              </Button>
            )}
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => setShowCommitDialog(true)}
              disabled={totalCount === 0}
            >
              <Send className="h-4 w-4 mr-1" /> Commit to Mail ({totalCount.toLocaleString()})
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Commit to Mail Dialog */}
      <AlertDialog open={showCommitDialog} onOpenChange={setShowCommitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Commit {totalCount.toLocaleString()} Records as Mailed</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                This will mark <strong>all {totalCount.toLocaleString()} filtered records</strong> as mailed.
                A mailing history entry will be created for each record with the campaign name and date below. This cannot be easily undone.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Campaign / Mailing Name *</label>
              <Input
                placeholder='e.g. "Post Card Clean", "Aggressive Insert"'
                value={commitCampaign}
                onChange={(e) => setCommitCampaign(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Mail Date *</label>
              <Input
                type="date"
                value={commitDate}
                onChange={(e) => setCommitDate(e.target.value)}
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={commitRunning}>Cancel</AlertDialogCancel>
            <Button
              onClick={handleCommitToMail}
              disabled={commitRunning || !commitCampaign.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {commitRunning ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Committing...</>
              ) : (
                <><Send className="h-4 w-4 mr-1" /> Commit {totalCount.toLocaleString()} Records</>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead className="w-8 py-1.5">
                    <Checkbox
                      checked={selectedIds.size === records.length && records.length > 0}
                      onCheckedChange={(checked) => {
                        setSelectedIds(checked ? new Set(records.map(r => r.id)) : new Set());
                      }}
                    />
                  </TableHead>
                  <SortableHead column="last_name" label="Name" />
                  <SortableHead column="county" label="County" />
                  <SortableHead column="origin_state" label="St" />
                  <SortableHead column="document_type" label="Doc Type" />
                  <SortableHead column="recorded_date" label="Recorded" />
                  <SortableHead column="consideration" label="Amt" />
                  <SortableHead column="times_mailed" label="Mailed" />
                  <SortableHead column="last_mailed_date" label="Last Mailed" />
                  <SortableHead column="mailing_status" label="Status" />
                  <TableHead className="py-1.5 w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-4 text-sm">Loading...</TableCell></TableRow>
                ) : records.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-4 text-sm text-muted-foreground">No records found</TableCell></TableRow>
                ) : records.map(r => (
                  <TableRow key={r.id} className="text-xs">
                    <TableCell className="py-1">
                      <Checkbox checked={selectedIds.has(r.id)} onCheckedChange={() => toggleSelect(r.id)} />
                    </TableCell>
                    <TableCell className="py-1 font-medium">
                      {r.first_name} {r.last_name}
                      {r.business_name && <span className="text-[10px] text-muted-foreground block leading-tight">{r.business_name}</span>}
                    </TableCell>
                    <TableCell className="py-1">{r.county}</TableCell>
                    <TableCell className="py-1">{r.origin_state}</TableCell>
                    <TableCell className="py-1">{r.document_type}</TableCell>
                    <TableCell className="py-1">{r.recorded_date ? new Date(r.recorded_date).toLocaleDateString() : ""}</TableCell>
                    <TableCell className="py-1">{r.consideration ? `$${r.consideration.toLocaleString()}` : ""}</TableCell>
                    <TableCell className="py-1">
                      <Badge variant={r.times_mailed === 0 ? "destructive" : "secondary"} className="text-[10px] px-1.5 py-0">
                        {r.times_mailed}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-1 text-muted-foreground">
                      {r.last_mailed_date || "—"}
                    </TableCell>
                    <TableCell className="py-1">
                      <Badge variant={r.mailing_status === "responded" ? "default" : r.mailing_status === "do_not_mail" ? "destructive" : "outline"} className="text-[10px] px-1.5 py-0">
                        {r.mailing_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-1">
                      <div className="flex gap-0.5">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setSelectedRecord(r)}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        {r.business_name && (
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setLookupRecord(r)} title="Look up business contact">
                            <Building2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive" title="Delete record">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this record?</AlertDialogTitle>
                              <AlertDialogDescription>This will permanently delete the record for {r.first_name} {r.last_name}.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteSingle(r.id)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount.toLocaleString()}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selectedRecord} onOpenChange={() => setSelectedRecord(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Record Detail</DialogTitle>
          </DialogHeader>
          {selectedRecord && <MailingRecordDetail record={selectedRecord} onClose={() => setSelectedRecord(null)} onUpdated={() => { fetchRecords(); fetchStats(); }} />}
        </DialogContent>
      </Dialog>

      {/* Business Lookup Dialog */}
      {lookupRecord && (
        <BusinessLookupDialog
          record={lookupRecord}
          open={!!lookupRecord}
          onOpenChange={(open) => { if (!open) setLookupRecord(null); }}
          onApply={handleApplyLookup}
        />
      )}
    </div>
  );
}
